/* eslint-disable @stylistic/quotes */
// ---------------------------------------------------------------------
// 🔒 AUTOGENERATED file for @synergy-design-system/components
// Please do not edit this file directly!
// It will get recreated when running pnpm build.
// ---------------------------------------------------------------------

// Global imports
import { modernNumericStrategy } from "../../components/input/strategies.js";

// Type imports
import type SynAccordion from "../../components/accordion/accordion.js";
import type SynAlert from "../../components/alert/alert.js";
import type SynBadge from "../../components/badge/badge.js";
import type SynButtonGroup from "../../components/button-group/button-group.js";
import type SynButton from "../../components/button/button.js";
import type SynCheckbox from "../../components/checkbox/checkbox.js";
import type SynCombobox from "../../components/combobox/combobox.js";
import type SynDetails from "../../components/details/details.js";
import type SynFile from "../../components/file/file.js";
import type SynIconButton from "../../components/icon-button/icon-button.js";
import type SynInput from "../../components/input/input.js";
import type SynOption from "../../components/option/option.js";
import type SynPagination from "../../components/pagination/pagination.js";
import type SynRadioButton from "../../components/radio-button/radio-button.js";
import type SynRadioGroup from "../../components/radio-group/radio-group.js";
import type SynRadio from "../../components/radio/radio.js";
import type SynRange from "../../components/range/range.js";
import type SynSelect from "../../components/select/select.js";
import type SynSideNav from "../../components/side-nav/side-nav.js";
import type SynSwitch from "../../components/switch/switch.js";
import type SynTagGroup from "../../components/tag-group/tag-group.js";
import type SynTag from "../../components/tag/tag.js";
import type SynTextarea from "../../components/textarea/textarea.js";
import type SynValidate from "../../components/validate/validate.js";
import type SynergyElement from "../../internal/synergy-element.js";
import type { SynDefaultChangedAttribute } from "../../events/events.js";

// Core types

/**
 * Custom helper type that adds decorated properties from
 * the globalSettings helper.
 */
export type GlobalSettingsEnabledElement = SynergyElement & {
  __originalDecoratedClassName: string;
  overrideGlobalSettings: (
    changedProperties: SynDefaultChangedAttribute[],
  ) => void;
};

/**
 * Allows for partial recursive types
 */
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends object ? RecursivePartial<T[P]> : T[P];
};

/**
 * Allowed value for a default setting
 * Gets the value of a given attribute for a given component
 */
export type AllowedValueForDefaultSetting<
  Elm extends SynergyElement,
  Attr extends keyof Elm,
> = Elm[Attr];

/**
 * List of all components that have default values
 */
export type ComponentNamesWithDefaultValues =
  | "SynAccordion"
  | "SynAlert"
  | "SynBadge"
  | "SynButton"
  | "SynButtonGroup"
  | "SynCheckbox"
  | "SynCombobox"
  | "SynDetails"
  | "SynFile"
  | "SynIconButton"
  | "SynInput"
  | "SynOption"
  | "SynPagination"
  | "SynRadio"
  | "SynRadioButton"
  | "SynRadioGroup"
  | "SynRange"
  | "SynSelect"
  | "SynSideNav"
  | "SynSwitch"
  | "SynTag"
  | "SynTagGroup"
  | "SynTextarea"
  | "SynValidate";

/**
 * Extracts all available default settings for a given component
 */
export type ExtractSettingsForElement<C extends SynergyElement> = {
  [key in keyof SynDefaultSettings]?: key extends keyof C
    ? AllowedValueForDefaultSetting<C, key>
    : never;
};

/**
 * Default settings map for all component values that have defaults set
 */
export type SynDefaultSettings = {
  delimiter: {
    SynCombobox?: AllowedValueForDefaultSetting<SynCombobox, "delimiter">;
    SynOption?: AllowedValueForDefaultSetting<SynOption, "delimiter">;
    SynSelect?: AllowedValueForDefaultSetting<SynSelect, "delimiter">;
  };
  numericStrategy: {
    SynInput?: AllowedValueForDefaultSetting<SynInput, "numericStrategy">;
  };
  size: {
    SynAccordion?: AllowedValueForDefaultSetting<SynAccordion, "size">;
    SynAlert?: AllowedValueForDefaultSetting<SynAlert, "size">;
    SynButton?: AllowedValueForDefaultSetting<SynButton, "size">;
    SynButtonGroup?: AllowedValueForDefaultSetting<SynButtonGroup, "size">;
    SynCheckbox?: AllowedValueForDefaultSetting<SynCheckbox, "size">;
    SynCombobox?: AllowedValueForDefaultSetting<SynCombobox, "size">;
    SynDetails?: AllowedValueForDefaultSetting<SynDetails, "size">;
    SynFile?: AllowedValueForDefaultSetting<SynFile, "size">;
    SynIconButton?: AllowedValueForDefaultSetting<SynIconButton, "size">;
    SynInput?: AllowedValueForDefaultSetting<SynInput, "size">;
    SynPagination?: AllowedValueForDefaultSetting<SynPagination, "size">;
    SynRadio?: AllowedValueForDefaultSetting<SynRadio, "size">;
    SynRadioButton?: AllowedValueForDefaultSetting<SynRadioButton, "size">;
    SynRadioGroup?: AllowedValueForDefaultSetting<SynRadioGroup, "size">;
    SynRange?: AllowedValueForDefaultSetting<SynRange, "size">;
    SynSelect?: AllowedValueForDefaultSetting<SynSelect, "size">;
    SynSwitch?: AllowedValueForDefaultSetting<SynSwitch, "size">;
    SynTag?: AllowedValueForDefaultSetting<SynTag, "size">;
    SynTagGroup?: AllowedValueForDefaultSetting<SynTagGroup, "size">;
    SynTextarea?: AllowedValueForDefaultSetting<SynTextarea, "size">;
  };
  variant: {
    SynAlert?: AllowedValueForDefaultSetting<SynAlert, "variant">;
    SynBadge?: AllowedValueForDefaultSetting<SynBadge, "variant">;
    SynButton?: AllowedValueForDefaultSetting<SynButton, "variant">;
    SynButtonGroup?: AllowedValueForDefaultSetting<SynButtonGroup, "variant">;
    SynPagination?: AllowedValueForDefaultSetting<SynPagination, "variant">;
    SynSideNav?: AllowedValueForDefaultSetting<SynSideNav, "variant">;
    SynValidate?: AllowedValueForDefaultSetting<SynValidate, "variant">;
  };
};

// Exports

/**
 * Default settings for all components
 */
export const defaultSettings: SynDefaultSettings = {
  delimiter: {
    SynCombobox: " ",
    SynOption: " ",
    SynSelect: " ",
  },
  numericStrategy: {
    SynInput: modernNumericStrategy,
  },
  size: {
    SynAccordion: "medium",
    SynAlert: "medium",
    SynButton: "medium",
    SynButtonGroup: "medium",
    SynCheckbox: "medium",
    SynCombobox: "medium",
    SynDetails: "medium",
    SynFile: "medium",
    SynIconButton: "inherit",
    SynInput: "medium",
    SynPagination: "medium",
    SynRadio: "medium",
    SynRadioButton: "medium",
    SynRadioGroup: "medium",
    SynRange: "medium",
    SynSelect: "medium",
    SynSwitch: "medium",
    SynTag: "medium",
    SynTagGroup: "medium",
    SynTextarea: "medium",
  },
  variant: {
    SynAlert: "primary",
    SynBadge: "primary",
    SynButton: "outline",
    SynButtonGroup: "outline",
    SynPagination: "full",
    SynSideNav: "default",
    SynValidate: "native",
  },
};

/**
 * Initial default settings for all components
 */
export const INITIAL_DEFAULT_SETTINGS: SynDefaultSettings = {
  delimiter: {
    SynCombobox: " ",
    SynOption: " ",
    SynSelect: " ",
  },
  numericStrategy: {
    SynInput: modernNumericStrategy,
  },
  size: {
    SynAccordion: "medium",
    SynAlert: "medium",
    SynButton: "medium",
    SynButtonGroup: "medium",
    SynCheckbox: "medium",
    SynCombobox: "medium",
    SynDetails: "medium",
    SynFile: "medium",
    SynIconButton: "inherit",
    SynInput: "medium",
    SynPagination: "medium",
    SynRadio: "medium",
    SynRadioButton: "medium",
    SynRadioGroup: "medium",
    SynRange: "medium",
    SynSelect: "medium",
    SynSwitch: "medium",
    SynTag: "medium",
    SynTagGroup: "medium",
    SynTextarea: "medium",
  },
  variant: {
    SynAlert: "primary",
    SynBadge: "primary",
    SynButton: "outline",
    SynButtonGroup: "outline",
    SynPagination: "full",
    SynSideNav: "default",
    SynValidate: "native",
  },
};
